import { AdvancedFieldRender, ElementDetails } from "@/lib/types";
import { FieldAlignItems } from "../ElementSettings/FieldAlignItems";
import { FieldFlex } from "../ElementSettings/FieldFlex";
import { FieldJustifyContent } from "../ElementSettings/FieldJustifyContent";
import { FieldSize } from "../ElementSettings/FieldSize";
import { Button } from "../ui/button";
import { useElements } from "@/hooks/useElementsProvider";
import { Label } from "../ui/label";
import { FormField, FormItem } from "../ui/form";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";

export const RowColumnPropsFields = (type: "Row" | "Column") =>
  ({
    defaultProps: {
      alignItems: "start",
      justifyContent: "start",
      width: "auto",
      height: "auto",
      padding: "0",
      gap: "0",
      paddingLeft: "0",
      paddingRight: "0",
      paddingTop: "0",
      paddingBottom: "0",
      marginLeft: "0",
      marginRight: "0",
      marginTop: "0",
      marginBottom: "0",
    },
    fields: [
      {
        prop: "css-settings",
        title: "",
        type: "advanced",
        config: {
          advancedRender: props => (
            <RowColumnSettingField {...props} direction={type} />
          ),
        },
      },
    ],
  }) satisfies Pick<ElementDetails, "defaultProps" | "fields">;

const RowColumnSettingField: AdvancedFieldRender<{
  direction: "Row" | "Column";
}> = ({ control, direction, id }) => {
  return (
    <div className="flex flex-col gap-4">
      <Accordion type="multiple">
        <AccordionItem value="flex">
          <AccordionTrigger>Flex</AccordionTrigger>
          <AccordionContent className="flex flex-col gap-4">
            <FormField
              control={control}
              name="alignItems"
              render={() => (
                <FormItem>
                  <ConvertType type={direction} id={id} />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="alignItems"
              render={({ field }) => (
                <FormItem>
                  <FieldAlignItems
                    direction={direction}
                    field={field}
                    id={id}
                    title="Align Items"
                    prop="alignItems"
                    type="custom"
                    description=""
                    config={{}}
                  />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="justifyContent"
              render={({ field }) => (
                <FormItem>
                  <FieldJustifyContent
                    direction={direction}
                    field={field}
                    id={id}
                    title="Justify Content"
                    prop="justifyContent"
                    type="custom"
                    description=""
                    config={{}}
                  />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="flex"
              render={({ field }) => (
                <FormItem>
                  <FieldFlex
                    field={field}
                    id={id}
                    title="Flex"
                    prop="flex"
                    type="custom"
                    description=""
                    config={{}}
                  />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="gap"
              render={({ field }) => (
                <FormItem>
                  <FieldSize
                    allowAuto
                    units={["px", "rem"]}
                    field={field}
                    id={id}
                    title="Gap"
                    prop="gap"
                    type="custom"
                    description=""
                    config={{}}
                  />
                </FormItem>
              )}
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="dimensions">
          <AccordionTrigger>Dimensions</AccordionTrigger>
          <AccordionContent className="flex flex-col gap-4">
            <FormField
              control={control}
              name="width"
              render={({ field }) => (
                <FormItem>
                  <FieldSize
                    allowAuto
                    units={["px", "vw", "%", "rem", "ch"]}
                    field={field}
                    id={id}
                    title="Width"
                    prop="width"
                    type="custom"
                    description=""
                    config={{}}
                  />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="height"
              render={({ field }) => (
                <FormItem>
                  <FieldSize
                    allowAuto
                    units={["px", "vh", "%", "rem", "ch"]}
                    field={field}
                    id={id}
                    title="Height"
                    prop="height"
                    type="custom"
                    description=""
                    config={{}}
                  />
                </FormItem>
              )}
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="padding">
          <AccordionTrigger>Padding</AccordionTrigger>
          <AccordionContent className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <FormField
                control={control}
                name="paddingLeft"
                render={({ field }) => (
                  <FormItem>
                    <FieldSize
                      allowAuto
                      units={["px", "rem"]}
                      field={field}
                      id={id}
                      title="Left"
                      prop="paddingLeft"
                      type="custom"
                      description=""
                      config={{}}
                    />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="paddingRight"
                render={({ field }) => (
                  <FormItem>
                    <FieldSize
                      allowAuto
                      units={["px", "rem"]}
                      field={field}
                      id={id}
                      title="Right"
                      prop="paddingRight"
                      type="custom"
                      description=""
                      config={{}}
                    />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex items-center gap-2">
              <FormField
                control={control}
                name="paddingTop"
                render={({ field }) => (
                  <FormItem>
                    <FieldSize
                      allowAuto
                      units={["px", "rem"]}
                      field={field}
                      id={id}
                      title="Top"
                      prop="paddingTop"
                      type="custom"
                      description=""
                      config={{}}
                    />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="paddingBottom"
                render={({ field }) => (
                  <FormItem>
                    <FieldSize
                      allowAuto
                      units={["px", "rem"]}
                      field={field}
                      id={id}
                      title="Bottom"
                      prop="paddingBottom"
                      type="custom"
                      description=""
                      config={{}}
                    />
                  </FormItem>
                )}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

const ConvertType = ({ id, type }: { id: string; type: "Row" | "Column" }) => {
  const { saveElements, elements } = useElements();

  const handleConvert = () => {
    if (!elements[id]) {
      return;
    }

    saveElements({
      ...elements,
      [id]: { ...elements[id], name: type === "Row" ? "Column" : "Row" },
    });
  };

  return (
    <div className="flex items-center justify-between">
      <Label>Change Direction</Label>
      <Button type="button" variant="outline" onClick={handleConvert}>
        {`Convert to ${type === "Row" ? "Column" : "Row"}`}
      </Button>
    </div>
  );
};
